import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clock, Flame, ChevronRight, CalendarClock } from "lucide-react";
import { istFull, IST_LABEL } from "../utils/time";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const impactColor = (i) => (i === "High" ? "#f87171" : i === "Medium" ? "#fbbf24" : "#64748b");

function fmtCountdown(ms) {
  if (ms <= 0) return { text: "LIVE NOW", live: true };
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const p = (n) => String(n).padStart(2, "0");
  if (d > 0) return { text: `${d}d ${p(h)}:${p(m)}:${p(sec)}`, live: false, soon: false };
  return { text: `${p(h)}:${p(m)}:${p(sec)}`, live: false, soon: h === 0 && m < 60 };
}

// Picks the next high-impact (fallback: key) upcoming economic event and counts down live.
const EventCountdown = ({ variant = "banner", onOpen, events: eventsProp }) => {
  const [events, setEvents] = useState(eventsProp || null);
  const [now, setNow] = useState(Date.now());
  const timer = useRef(null);

  useEffect(() => {
    if (eventsProp) { setEvents(eventsProp); return; }
    (async () => {
      try {
        const d = await (await fetch(`${API}/news/calendar`, { credentials: "include" })).json();
        setEvents(d.events || []);
      } catch { setEvents([]); }
    })();
  }, [eventsProp]);

  useEffect(() => {
    timer.current = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer.current);
  }, []);

  const next = useMemo(() => {
    if (!events?.length) return null;
    const nowS = now / 1000;
    const future = events.filter((e) => e.sort_ts && e.sort_ts > nowS);
    const high = future.filter((e) => e.impact === "High").sort((a, b) => a.sort_ts - b.sort_ts);
    if (high[0]) return high[0];
    const key = future.filter((e) => e.key).sort((a, b) => a.sort_ts - b.sort_ts);
    if (key[0]) return key[0];
    return future.sort((a, b) => a.sort_ts - b.sort_ts)[0] || null;
  }, [events, now]);

  if (!next) return null;
  const cd = fmtCountdown(next.sort_ts * 1000 - now);
  const col = impactColor(next.impact);

  if (variant === "strip") {
    return (
      <button
        onClick={() => onOpen && onOpen(next)}
        data-testid="event-countdown-strip"
        className={`ws-evt-strip ${cd.soon || cd.live ? "soon" : ""}`}
        title={`${next.currency} · ${next.title} · ${next.impact} impact`}
      >
        <span className="ws-evt-dot" style={{ background: col }} />
        <Clock className="w-3.5 h-3.5" style={{ color: col }} />
        <span className="ws-evt-strip-ccy">{next.currency}</span>
        <span className="ws-evt-strip-title">{next.title}</span>
        <span className="ws-evt-strip-cd" style={{ color: cd.soon || cd.live ? col : undefined }}>
          {cd.live ? "LIVE NOW" : `in ${cd.text}`}
        </span>
      </button>
    );
  }

  // banner
  return (
    <button
      onClick={() => onOpen && onOpen(next)}
      data-testid="event-countdown-banner"
      className={`ws-evt-banner ${cd.soon || cd.live ? "soon" : ""}`}
    >
      <div className="ws-evt-banner-left">
        <div className="ws-evt-banner-icon" style={{ color: col }}>
          {next.key ? <Flame className="w-5 h-5" /> : <CalendarClock className="w-5 h-5" />}
        </div>
        <div className="min-w-0 text-left">
          <div className="ws-evt-banner-lbl">
            <span className="ws-evt-dot" style={{ background: col }} /> Next high-impact event
          </div>
          <div className="ws-evt-banner-title">
            <span className="ws-evt-banner-ccy" style={{ color: col, borderColor: col + "55", background: col + "18" }}>{next.currency}</span>
            {next.title}
          </div>
          <div className="ws-evt-banner-meta">
            {istFull(next.sort_ts)} {IST_LABEL}
            {next.forecast ? ` · Forecast ${next.forecast}` : ""}{next.previous ? ` · Prev ${next.previous}` : ""}
          </div>
        </div>
      </div>
      <div className="ws-evt-banner-right">
        <div className="ws-evt-banner-cd" style={{ color: cd.soon || cd.live ? col : undefined }} data-testid="event-countdown-value">
          {cd.text}
        </div>
        <div className="ws-evt-banner-cta">View AI take <ChevronRight className="w-3.5 h-3.5" /></div>
      </div>
    </button>
  );
};

export default EventCountdown;
